import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { boutiqueService } from '../services/boutiqueService';
import { useAuth } from './AuthContext';

const BoutiqueContext = createContext();

export const useBoutique = () => {
  const context = useContext(BoutiqueContext);
  if (!context) {
    throw new Error('useBoutique must be used within a BoutiqueProvider');
  }
  return context;
};

export const BoutiqueProvider = ({ children }) => {
  const { user, isSuperAdmin, isAuthenticated } = useAuth();
  const [currentBoutique, setCurrentBoutique] = useState(null);
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Ref pour éviter les boucles infinies
  const hasInitialized = useRef(false);
  const lastUserId = useRef(null);

  // Charger les boutiques une seule fois au chargement
  useEffect(() => {
    if (isAuthenticated && user && user.id !== lastUserId.current && !hasInitialized.current) {
      lastUserId.current = user.id;
      hasInitialized.current = true;
      initializeBoutique();
    }
  }, [isAuthenticated, user?.id]);

  // ✅ NOUVELLE: Fonction pour récupérer la boutique actuelle via l'API
  const loadCurrentBoutiqueInfo = async () => {
    try {
      const response = await boutiqueService.getCurrentBoutiqueInfo();
      
      if (response.success && response.data) {
        
        // Mapper les données de l'API vers notre format
        const boutiqueFromAPI = {
          id: response.data.id || response.data.boutique_id,
          name: response.data.nom || response.data.name || response.data.boutique_name || 'Ma Boutique',
          status: response.data.status || 'ACTIVE',
          primaryColor: response.data.primary_color || response.data.couleur_principale || '#3b82f6',
          secondaryColor: response.data.secondary_color || response.data.couleur_secondaire || '#1d4ed8',
          address: response.data.adresse || response.data.address || '',
          phone: response.data.telephone || response.data.phone || '',
          phone1: response.data.phone1 || response.data.telephone1 || '',
          email: response.data.email || '',
          ville: response.data.ville || '',
          description: response.data.description || '',
          logoUrl: response.data.logoUrl || response.data.logo_url,

           // ✅ AJOUT ATELIER - ajoute cette ligne
          atelierEnabled: response.data.atelierEnabled === true || response.data.atelier_enabled === true,
          restaurantModuleActive: response.data.restaurantModuleActive === true || response.data.restaurant_module_active === true,
          // ✅ DOUBLE PRIX (générique) - manquait, c'est la cause du bug
          dualPriceEnabled: response.data.dualPriceEnabled === true || response.data.dual_price_enabled === true,
          price1Label: response.data.price1Label || response.data.price1_label || 'Prix de vente',
          price2Label: response.data.price2Label || response.data.price2_label || 'Prix 2',
          
           // Données supplémentaires
          devise: response.data.devise || 'XOF',
          fuseauHoraire: response.data.fuseauHoraire || response.data.fuseau_horaire || 'Africa/Dakar',
          dateCreation: response.data.dateCreation || response.data.date_creation,
          derniereModification: response.data.derniereModification || response.data.derniere_modification
        };
        
        setCurrentBoutique(boutiqueFromAPI);
        setBoutiques([boutiqueFromAPI]);
        return boutiqueFromAPI;
      } else {
        console.warn('⚠️ Échec récupération boutique via API:', response.message);
        return null;
      }
    } catch (error) {
      console.error('❌ Erreur loadCurrentBoutiqueInfo:', error);
      return null;
    }
  };

  // ✅ MODIFIÉE: Fonction d'initialisation avec fallback intelligent
  const initializeBoutique = async () => {
    try {
      

      setLoading(true);
      setError(null);

      if (isSuperAdmin) {
        // Super admin : récupérer toutes les boutiques
        try {
          const response = await boutiqueService.getAllBoutiques();
          if (response.success) {
            const boutiquesData = response.data?.boutiques || response.data || [];
            setBoutiques(boutiquesData);
            if (boutiquesData.length > 0) {
              setCurrentBoutique(boutiquesData[0]);
            }
          } else {
            setError(response.message);
          }
        } catch (apiError) {
          console.error('❌ Erreur API super admin:', apiError);
          setError('Erreur lors du chargement des boutiques');
        }
      } else {
        // Manager/Employee : essayer d'abord l'API, puis fallback sur user data
        
        const boutiqueFromAPI = await loadCurrentBoutiqueInfo();
        
        if (boutiqueFromAPI) {
        } else {
          
          // Fallback : créer boutique à partir des données utilisateur
          if (user.boutique_id) {
            const boutique = {
              id: user.boutique_id,
              name: user.boutique_name || 'Ma Boutique',
              status: 'ACTIVE',
              primaryColor: '#3b82f6',
              secondaryColor: '#1d4ed8',
              address: user.boutique_address || '',
              phone: user.boutique_phone || '',
              phone1: user.boutique_phone1 || '',
              email: user.boutique_email || '',
              ville: user.boutique_ville || '',
              description: user.boutique_description || '',
              atelierEnabled: user.atelierEnabled === true || user.atelier_enabled === true, // ✅ AJOUT
              restaurantModuleActive: currentBoutique?.restaurantModuleActive === true,
              isRestaurantEnabled: currentBoutique?.restaurantModuleActive === true,
              // ✅ DOUBLE PRIX (générique)
              dualPriceEnabled: user.dualPriceEnabled === true || user.dual_price_enabled === true,
              price1Label: user.price1Label || user.price1_label || 'Prix de vente',
              price2Label: user.price2Label || user.price2_label || 'Prix 2',
            };
            
            setCurrentBoutique(boutique);
            setBoutiques([boutique]);
          } else if (user.boutiqueId) {
            const boutique = {
              id: user.boutiqueId,
              name: user.boutiqueName || user.boutique_name || 'Ma Boutique',
              status: 'ACTIVE',
              primaryColor: '#3b82f6',
              secondaryColor: '#1d4ed8',
              atelierEnabled: user.atelierEnabled === true || user.atelier_enabled === true, // ✅ AJOUT
              restaurantModuleActive: user.restaurantModuleActive === true,
              // ✅ DOUBLE PRIX (générique)
              dualPriceEnabled: user.dualPriceEnabled === true || user.dual_price_enabled === true,
              price1Label: user.price1Label || user.price1_label || 'Prix de vente',
              price2Label: user.price2Label || user.price2_label || 'Prix 2'

            };
            
            setCurrentBoutique(boutique);
            setBoutiques([boutique]);
          } else {
            console.warn('⚠️ Utilisateur sans boutique_id:', user);
            setError('Utilisateur non associé à une boutique');
          }
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation boutique:', error);
      setError('Erreur lors de l\'initialisation de la boutique');
    } finally {
      setLoading(false);
    }
  };

  // Reset quand l'utilisateur change (déconnexion/reconnexion)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      hasInitialized.current = false;
      lastUserId.current = null;
      setCurrentBoutique(null);
      setBoutiques([]);
      setError(null);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // ✅ NOUVELLE: Fonction pour recharger les infos boutique
  const refreshCurrentBoutique = async () => {
    if (!isAuthenticated || !user || isSuperAdmin) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const boutiqueFromAPI = await loadCurrentBoutiqueInfo();
      
      if (boutiqueFromAPI) {
        return { success: true, data: boutiqueFromAPI };
      } else {
        console.warn('⚠️ Impossible de rafraîchir la boutique');
        return { success: false, error: 'Impossible de récupérer les informations de la boutique' };
      }
    } catch (error) {
      console.error('❌ Erreur refreshCurrentBoutique:', error);
      setError('Erreur lors du rafraîchissement de la boutique');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const createBoutique = async (boutiqueData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await boutiqueService.createBoutique(boutiqueData);
      
      if (response.success) {
        setBoutiques(prev => [...prev, response.data]);
        return { success: true, data: response.data };
      } else {
        setError(response.message);
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la création';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateBoutique = async (boutiqueId, updateData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await boutiqueService.updateBoutique(boutiqueId, updateData);
      
      if (response.success) {
        setBoutiques(prev => 
          prev.map(b => b.id === boutiqueId ? response.data : b)
        );
        
        if (currentBoutique?.id === boutiqueId) {
          setCurrentBoutique(response.data);
        }
        
        return { success: true, data: response.data };
      } else {
        setError(response.message);
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const archiveBoutique = async (boutiqueId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await boutiqueService.archiveBoutique(boutiqueId);
      
      if (response.success) {
        setBoutiques(prev => 
          prev.map(b => b.id === boutiqueId ? 
            { ...b, status: 'ARCHIVED' } : b)
        );
        return { success: true };
      } else {
        setError(response.message);
        return { success: false, error: response.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'archivage';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const switchBoutique = (boutique) => {
    if (isSuperAdmin) {
      setCurrentBoutique(boutique);
      localStorage.setItem('selectedBoutique', JSON.stringify(boutique));
    }
  };

  const getBoutiqueTheme = () => {
    if (!currentBoutique) {
      return {
        primaryColor: '#3b82f6',
        secondaryColor: '#1d4ed8',
        logoUrl: null
      };
    }
    
    return {
      primaryColor: currentBoutique.primaryColor || '#3b82f6',
      secondaryColor: currentBoutique.secondaryColor || '#1d4ed8',
      logoUrl: currentBoutique.logoUrl
    };
  };

  const getActiveBoutiques = () => {
    return boutiques.filter(b => b.status === 'ACTIVE');
  };

  const getArchivedBoutiques = () => {
    return boutiques.filter(b => b.status === 'ARCHIVED');
  };

  // ✅ MODIFIÉE: Fonction de reload manuelle améliorée
  const reloadBoutiques = async () => {
    if (hasInitialized.current) {
      hasInitialized.current = false;
      await initializeBoutique();
      hasInitialized.current = true;
    }
  };

  // ✅ NOUVELLE: Vérifier si la boutique actuelle est complète
  const isCurrentBoutiqueComplete = () => {
    if (!currentBoutique) return false;
    
    const requiredFields = ['id', 'name'];
    const hasRequiredFields = requiredFields.every(field => currentBoutique[field]);
    
    // Vérifier si on a des données détaillées (pas juste user data)
    const hasDetailedData = currentBoutique.dateCreation || 
                           currentBoutique.description !== undefined ||
                           currentBoutique.devise;
    
    return hasRequiredFields && hasDetailedData;
  };

  const value = {
    // État
    currentBoutique,
    boutiques,
    loading,
    error,
    
    // Actions
    reloadBoutiques,
    refreshCurrentBoutique, // ✅ NOUVELLE
    createBoutique,
    updateBoutique,
    archiveBoutique,
    switchBoutique,
    
    // Helpers
    getBoutiqueTheme,
    getActiveBoutiques,
    getArchivedBoutiques,
    isCurrentBoutiqueComplete, // ✅ NOUVELLE
    canManageBoutique: isSuperAdmin || (user?.boutique_id === currentBoutique?.id || user?.boutiqueId === currentBoutique?.id),
    // ✅ AJOUT ATELIER
    isAtelierEnabled: currentBoutique?.atelierEnabled === true,
    isRestaurantEnabled: currentBoutique?.restaurantModuleActive === true,
    // ✅ DOUBLE PRIX (générique, indépendant du module Restaurant)
    isDualPriceEnabled: currentBoutique?.dualPriceEnabled === true,
    price1Label: currentBoutique?.price1Label || 'Prix de vente',
    price2Label: currentBoutique?.price2Label || 'Prix 2',
    
    // Stats
    totalBoutiques: boutiques.length,
    activeBoutiques: getActiveBoutiques().length,
    archivedBoutiques: getArchivedBoutiques().length,
    
    // Clear error
    clearError: () => setError(null)
  };

  return (
    <BoutiqueContext.Provider value={value}>
      {children}
    </BoutiqueContext.Provider>
  );
};